// src/pages/Review.jsx
// Expert Review Dashboard — sits between Notes and Statements
// Phase R3: Deferred Tax card added — shows computed vs TB, per-asset workings,
//           traffic light (green/amber/red), and SARS IT-180 rate table.

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { generateReview, confirmDisclosureChecklist, saveJobProgress, getTaxRateSuggestion, confirmTaxRate, getJobTaxRateHistory, getGoingConcernStatus, setGoingConcernOverride, previewTaxProvision, saveTaxProvisionDecision } from '../api/kailux'
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import BackButton from '../components/BackButton'
import { isUnmappedAccount, buildFiguresFromAccounts } from '../utils/accountMapping'
import { applyTaxProvision, extractTaxProvision, withoutTaxProvisionFigures, basisLabel, provisionAmountError } from '../utils/taxProvision'
import SbcConfirmation from '../components/SbcConfirmation'
import TaxBasisCard from '../components/TaxBasisCard'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const CHECKLIST_ITEMS = [
  { id:'going_concern',    label:'Going concern basis confirmed',                          sub:'The entity can continue operations for the foreseeable future' },
  { id:'related_party',    label:'Related party transactions reviewed and disclosed',      sub:'All transactions with directors, shareholders and associates have been identified' },
  { id:'post_events',      label:'Events after the reporting date reviewed',               sub:'No undisclosed material events have occurred since year end' },
  { id:'dir_remuneration', label:"Directors' remuneration separately identified",          sub:"Director/member/trustee remuneration is disclosed in the notes" },
  { id:'tax_computation',  label:'Tax computation reviewed',                              sub:'Income tax expense and deferred tax have been verified' },
  { id:'tb_agree',         label:'All figures agree to the trial balance',                sub:'Every amount in the statements traces back to the source data' },
  { id:'comparatives',     label:'Prior year comparatives confirmed as accurate',         sub:'Comparative figures agree to the prior year signed financial statements' },
]

const RATIO_META = [
  {
    key:'current_ratio',
    label:'Current Ratio',
    desc:'Current assets ÷ current liabilities',
    format: v => v === null ? '—' : v.toFixed(2) + 'x',
    benchmark: v => v === null ? 'grey' : v >= 1.5 ? 'green' : v >= 1.0 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v >= 1.5 ? 'Healthy (above 1.5x)' : v >= 1.0 ? 'Watch (between 1.0–1.5x)' : 'Risk (below 1.0x)',
  },
  {
    key:'quick_ratio',
    label:'Quick Ratio',
    desc:'(Cash + receivables) ÷ current liabilities',
    format: v => v === null ? '—' : v.toFixed(2) + 'x',
    benchmark: v => v === null ? 'grey' : v >= 1.0 ? 'green' : v >= 0.7 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v >= 1.0 ? 'Healthy (above 1.0x)' : v >= 0.7 ? 'Watch (0.7–1.0x)' : 'Risk (below 0.7x)',
  },
  {
    key:'gp_margin',
    label:'Gross Profit Margin',
    desc:'Gross profit ÷ revenue × 100',
    format: v => v === null ? '—' : v.toFixed(1) + '%',
    benchmark: v => v === null ? 'grey' : v >= 30 ? 'green' : v >= 15 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v >= 30 ? 'Strong margin' : v >= 15 ? 'Average margin' : 'Low margin',
  },
  {
    key:'np_margin',
    label:'Net Profit Margin',
    desc:'Profit after tax ÷ revenue × 100',
    format: v => v === null ? '—' : v.toFixed(1) + '%',
    benchmark: v => v === null ? 'grey' : v >= 10 ? 'green' : v >= 3 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v >= 10 ? 'Strong profitability' : v >= 3 ? 'Moderate profitability' : v >= 0 ? 'Thin margins' : 'Loss-making',
  },
  {
    key:'debt_to_equity',
    label:'Debt-to-Equity',
    desc:'Total liabilities ÷ total equity',
    format: v => v === null ? '—' : v.toFixed(2) + 'x',
    benchmark: v => v === null ? 'grey' : v <= 1.0 ? 'green' : v <= 2.0 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v <= 1.0 ? 'Low leverage' : v <= 2.0 ? 'Moderate leverage' : 'High leverage',
  },
  {
    key:'roe',
    label:'Return on Equity',
    desc:'Profit after tax ÷ equity × 100',
    format: v => v === null ? '—' : v.toFixed(1) + '%',
    benchmark: v => v === null ? 'grey' : v >= 15 ? 'green' : v >= 5 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v >= 15 ? 'Strong return' : v >= 5 ? 'Moderate return' : v >= 0 ? 'Low return' : 'Negative return',
  },
  {
    key:'dso',
    label:'Days Sales Outstanding',
    desc:'Receivables ÷ revenue × 365',
    format: v => v === null ? '—' : Math.round(v) + ' days',
    benchmark: v => v === null ? 'grey' : v <= 30 ? 'green' : v <= 60 ? 'amber' : 'red',
    benchmarkLabel: v => v === null ? 'No data' : v <= 30 ? 'Fast collection' : v <= 60 ? 'Average collection' : 'Slow collection',
  },
  {
    key:'dpo',
    label:'Days Payable Outstanding',
    desc:'Payables ÷ cost of sales × 365',
    format: v => v === null ? '—' : Math.round(v) + ' days',
    benchmark: v => v === null ? 'grey' : v >= 30 && v <= 60 ? 'green' : v > 60 ? 'amber' : 'amber',
    benchmarkLabel: v => v === null ? 'No data' : v >= 30 && v <= 60 ? 'Normal payment terms' : v > 60 ? 'Extended payment terms' : 'Fast payment',
  },
]

const BENCH_COLORS = {
  green: { bg: T.gnd, border: alpha(T.gn, 0.25), text: T.gn, dot: T.gn },
  amber: { bg: T.yld, border: alpha(T.am, 0.25),  text: T.yl, dot: T.yl },
  red:   { bg: T.rdd, border: alpha(T.rd, 0.25),   text: T.rd, dot: T.rd },
  grey:  { bg: T.s2,  border: T.bd,                     text: T.mt, dot: T.mt },
}

const DT_TRAFFIC = {
  green: { bg: T.gnd, border: alpha(T.gn, 0.25), text: T.gn, label: 'Agrees',              icon: CheckCircle2 },
  amber: { bg: T.yld, border: alpha(T.am, 0.25), text: T.yl, label: 'Review required', icon: AlertTriangle },
  red:   { bg: T.rdd, border: alpha(T.rd, 0.25),  text: T.rd, label: 'Significant variance', icon: XCircle },
}

export function fmtR(n) {
  if (n === null || n === undefined || n === 0) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `R${(abs/1_000_000).toFixed(2)}m`
  if (abs >= 1_000)     return `R${abs.toLocaleString('en-ZA', {maximumFractionDigits:0})}`
  return `R${abs.toFixed(0)}`
}

export function trendArrow(current, prior, higherIsBetter = true) {
  if (current === null || prior === null) return null
  const improved = higherIsBetter ? current > prior : current < prior
  const same     = Math.abs(current - prior) < 0.01
  if (same) return { symbol: '→', color: T.mt }
  return improved
    ? { symbol: '↑', color: T.gn }
    : { symbol: '↓', color: T.rd }
}

const RATIO_TREND_DIRECTION = {
  current_ratio:  true,
  quick_ratio:    true,
  gp_margin:      true,
  np_margin:      true,
  debt_to_equity: false,
  roe:            true,
  dso:            false,
  dpo:            null,
}

// ── Tax Rate Card (Phase 10, Jul 2026 — Statutory Rate Configuration) ─────────
// Lets the preparer confirm or override the effective SA corporate tax rate
// for THIS specific engagement — matters most for a genuinely historical AFS
// (Phase 1's free date-range picker), where today's rate isn't necessarily
// the rate that applied at that year-end. Confirming here persists the
// decision onto the job record itself, so it stays correct/auditable even
// if the global statutory rate table is later corrected.
function TaxRateCard({ clientId, financialYear, yearEnd, onConfirmed }) {
  const [suggestion, setSuggestion] = useState(null)
  const [rateInput,  setRateInput]  = useState('')
  const [loading,    setLoading]    = useState(true)
  const [saving,     setSaving]     = useState(false)
  const [editing,    setEditing]    = useState(false)
  // 22 Jul 2026 follow-up: per-job rate history (kailux-master-plan.md §4 #12) —
  // what rate was used for each PAST generation of this same job, not just
  // today's current value. Fetched lazily, only when the preparer asks to see it.
  const [showHistory, setShowHistory] = useState(false)
  const [history,      setHistory]    = useState(null)
  const [historyLoading, setHistoryLoading] = useState(false)

  useEffect(() => {
    if (!clientId || !financialYear) { setLoading(false); return }
    setLoading(true)
    getTaxRateSuggestion(clientId, financialYear, yearEnd || '')
      .then(d => {
        setSuggestion(d)
        setRateInput((d.tax_rate * 100).toFixed(2).replace(/\.00$/, ''))
      })
      .catch(() => setSuggestion(null))
      .finally(() => setLoading(false))
  }, [clientId, financialYear, yearEnd])

  const toggleHistory = () => {
    if (showHistory) { setShowHistory(false); return }
    setShowHistory(true)
    if (history === null) {
      setHistoryLoading(true)
      getJobTaxRateHistory(clientId, financialYear)
        .then(d => setHistory(d.history || []))
        .catch(() => setHistory([]))
        .finally(() => setHistoryLoading(false))
    }
  }

  const confirm = async () => {
    const pct = parseFloat(rateInput)
    if (Number.isNaN(pct) || pct <= 0 || pct >= 100) {
      alert('Enter a valid tax rate as a percentage, e.g. 27')
      return
    }
    setSaving(true)
    try {
      await confirmTaxRate(clientId, financialYear, pct / 100, suggestion?.wta_rate_overrides || {})
      const refreshed = await getTaxRateSuggestion(clientId, financialYear, yearEnd || '')
      setSuggestion(refreshed)
      setEditing(false)
      setHistory(null) // stale now — refetch next time the preparer opens it
      if (onConfirmed) onConfirmed()
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not confirm this rate.')
    } finally {
      setSaving(false)
    }
  }

  if (loading || !suggestion) return null

  return (
    <div style={{ marginBottom:'16px' }}>
    <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
      padding:'14px 18px', display:'flex', alignItems:'center',
      justifyContent:'space-between', flexWrap:'wrap', gap:'10px' }}>
      <div>
        <div style={{ fontSize:'12px', fontWeight:'700', color:T.mt, marginBottom:'2px' }}>
          EFFECTIVE TAX RATE FOR THIS ENGAGEMENT
        </div>
        <div style={{ fontSize:'12px', color:T.mt }}>
          {suggestion.already_confirmed
            ? `Confirmed at ${(suggestion.tax_rate * 100).toFixed(2).replace(/\.00$/,'')}% by ${suggestion.confirmed_by || 'a previous session'}`
            : `Suggested from ${suggestion.source === 'statutory_rates_table' ? 'the statutory rate table for this year-end' : 'the platform default'} — confirm or override before generating`}
        </div>
      </div>
      {editing ? (
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <input type="number" step="0.01" value={rateInput}
            onChange={e => setRateInput(e.target.value)}
            style={{ width:'80px', padding:'8px 10px', background:T.s3, border:`1px solid ${T.bd}`,
              borderRadius:'8px', color:T.tx, fontSize:'13px', boxSizing:'border-box' }} />
          <span style={{ color:T.mt, fontSize:'13px' }}>%</span>
          <button onClick={confirm} disabled={saving}
            style={{ padding:'8px 14px', borderRadius:'8px', border:'none', background:T.prg,
              color:T.onpri, fontSize:'13px', fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving…' : 'Confirm'}
          </button>
          <button onClick={() => setEditing(false)}
            style={{ padding:'8px 14px', borderRadius:'8px', border:`1px solid ${T.bd}`,
              background:'transparent', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
            Cancel
          </button>
        </div>
      ) : (
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ fontSize:'20px', fontWeight:'800', color:T.tx, fontFamily:"'DM Mono',monospace" }}>
            {(suggestion.tax_rate * 100).toFixed(2).replace(/\.00$/,'')}%
          </div>
          <button onClick={() => setEditing(true)}
            style={{ padding:'8px 14px', borderRadius:'8px', border:`1px solid ${T.bd}`,
              background:'transparent', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
            {suggestion.already_confirmed ? 'Override' : 'Confirm / override'}
          </button>
          <button onClick={toggleHistory}
            style={{ padding:'8px 12px', borderRadius:'8px', border:'none',
              background:'transparent', color:T.mt, fontSize:'12px', cursor:'pointer', textDecoration:'underline' }}>
            {showHistory ? 'Hide history' : 'View history'}
          </button>
        </div>
      )}
    </div>
    {showHistory && (
      <div style={{ background:T.s2, border:`1px solid ${T.bd}`, borderTop:'none', borderRadius:'0 0 12px 12px',
        padding:'12px 18px', fontSize:'12px' }}>
        {historyLoading ? (
          <div style={{ color:T.mt }}>Loading history…</div>
        ) : !history || history.length === 0 ? (
          <div style={{ color:T.mt }}>No confirmation history yet for this engagement.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            <div style={{ color:T.mt, fontWeight:700, marginBottom:'2px' }}>
              Past confirmations for this job — what rate was used for each past generation:
            </div>
            {history.map((h, i) => (
              <div key={i} style={{ color:T.lt, display:'flex', gap:'10px', alignItems:'baseline' }}>
                <span style={{ fontFamily:"'DM Mono',monospace", color:T.tx, minWidth:'50px' }}>
                  {(h.tax_rate * 100).toFixed(2).replace(/\.00$/,'')}%
                </span>
                <span>
                  {h.source === 'accountant_confirmed' ? 'confirmed by' : 'auto-recorded at generation by'}{' '}
                  {h.confirmed_by} — {h.confirmed_at ? new Date(h.confirmed_at).toLocaleString('en-ZA') : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    )}
    </div>
  )
}

// ── Tax Provision Card ──────────────────────────────────────────────────────
// 29 Sep 2026 (§88). Income tax only ever enters the statements from a mapped
// "Income Tax Expense" account, so a run that shows a profit but no tax at all
// silently presents pre-tax profit as the profit for the year. KaiLux never
// adds tax on its own: it SUGGESTS an indicative amount (profit before tax x the
// engagement's rate) and the preparer must explicitly choose — add it (and may
// edit the amount) or don't add it (and say why). Both answers are recorded on
// the job with who/when; Proceed is blocked until one is given.
const fmtRand = (n) => `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// §108 (Oct 2026): when the server returns `options`, the card shows the STANDARD company-rate amount
// and the SMALL BUSINESS CORPORATION (SBC) amount side by side and selects NEITHER. SBC needs the
// preparer's explicit eligibility confirmation (never pre-ticked; the client-level "last confirmed
// for FY…" hint only reminds), is capped at the standard amount, and is unavailable — with the reason
// and a link to the admin screen — when no verified table covers the year-end. Without `options`
// (an older server) the card behaves exactly as before. The chosen basis is recorded on the engagement.
function BasisOption({ testid, title, subtitle, amount, children, actionLabel, disabled, busy, onPick, btn }) {
  return (
    <div data-testid={testid} style={{ flex:'1 1 260px', minWidth:'240px', background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'10px', padding:'12px 14px' }}>
      <div style={{ fontSize:'13px', fontWeight:'700', color:T.tx }}>{title}</div>
      <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px' }}>{subtitle}</div>
      {amount != null && <div style={{ fontSize:'20px', fontWeight:'800', color:T.tx, marginBottom:'6px' }}>{fmtRand(amount)}</div>}
      {children}
      <button type="button" style={{ ...btn(false), marginTop:'10px' }} disabled={busy || disabled} onClick={onPick}>{actionLabel}</button>
    </div>
  )
}

function TaxProvisionCard({ preview, decision, appliedAmount, busy, error, onAdd, onDecline, onUndo, deferredMovement = 0 }) {
  const [mode, setMode]               = useState(null)    // null | 'add' | 'decline'
  const [changing, setChanging]       = useState(false)
  const [amountInput, setAmountInput] = useState('')
  const [reasonInput, setReasonInput] = useState('')
  const [basis, setBasis]             = useState(null)    // null (older server) | 'standard' | 'sbc'
  const [sbcConfirmed, setSbcConfirmed] = useState(false)

  useEffect(() => {
    if (preview?.indicative_amount) setAmountInput(String(preview.indicative_amount))
  }, [preview?.indicative_amount])

  if (!preview) return null
  const addedOk    = decision?.decision === 'added' && appliedAmount > 0
  const declinedOk = decision?.decision === 'declined'
  if (!preview.applies && !addedOk && !declinedOk) return null

  const options    = preview.options || null
  const sbcOpt     = options?.sbc || null
  const pending    = !addedOk && !declinedOk
  const showChoice = pending || changing
  const rate       = (preview.rate * 100).toFixed(2).replace(/\.?0+$/, '')
  const amountNum  = parseFloat(amountInput)
  const amountErr  = provisionAmountError(amountNum, preview, basis, fmtRand)
  const reasonOk   = reasonInput.trim().length >= 5
  const sbcBlocked = basis === 'sbc' && !sbcConfirmed
  const basisNow   = decision?.basis || preview.basis?.basis || null
  // The amount entered is the TOTAL tax charge for the year; the statements show
  // current tax as that total less the deferred tax movement already carried in
  // the trial balance. A total below that movement would print a NEGATIVE
  // "current tax" in the tax note, so say so before it happens (non-blocking —
  // a genuine credit is possible, and the effective-rate check also flags it).
  const belowDeferred = deferredMovement > 0 && !amountErr && amountNum < deferredMovement

  const box = { background: pending ? T.yld : alpha(T.gn, 0.06), border: `1px solid ${pending ? alpha(T.am, 0.35) : alpha(T.gn, 0.3)}`,
    borderRadius:'12px', padding:'16px 20px', marginBottom:'24px' }
  const btn = (primary) => ({ padding:'8px 14px', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor: busy ? 'default' : 'pointer',
    border: primary ? 'none' : `1px solid ${T.bd}`, background: primary ? T.prg : 'transparent', color: primary ? T.onpri : T.lt,
    opacity: busy ? 0.6 : 1 })

  const pickBasis = (b) => {
    setBasis(b); setSbcConfirmed(false); setMode('add')
    const opt = b === 'sbc' ? sbcOpt : options?.standard
    setAmountInput(String(opt?.amount ?? preview.indicative_amount))
  }
  const resetChoice = () => { setMode(null); setBasis(null); setSbcConfirmed(false) }

  return (
    <div style={box} data-testid="tax-provision-card">
      <div style={{ fontSize:'12px', fontWeight:'700', letterSpacing:'1.5px', marginBottom:'8px', color: pending ? T.yl : T.gn }}>
        {pending ? <><Icon name="alert" /> NO INCOME TAX RECORDED</> : <><Icon name="check" /> INCOME TAX PROVISION</>}
      </div>

      {pending && (
        <div style={{ fontSize:'14px', color:T.tx, lineHeight:1.55, marginBottom:'12px' }}>
          Your figures show a profit before tax of <strong>{fmtRand(preview.pbt)}</strong> but no income tax expense or tax payable.
          Unless you confirm otherwise, the statements will present this pre-tax profit as the profit for the year.
          At the {rate}% rate{preview.rate_confirmed ? '' : ' (not yet confirmed for this engagement)'}, an indicative provision would be about{' '}
          <strong>{fmtRand(preview.indicative_amount)}</strong> — profit before tax × rate, before any adjustments for assessed losses,
          non-deductible items or temporary differences. Nothing is added unless you choose to.
          {sbcOpt && <> If the company qualifies as a small business corporation, its sliding-scale amount is shown below as an alternative;
            it needs your confirmation first, and you choose which basis to use.</>}
          {deferredMovement > 0 && (
            <> Your trial balance already carries a deferred tax movement of <strong>{fmtRand(deferredMovement)}</strong> this year; the
            amount you confirm is the <em>total</em> charge (current + deferred), so that movement is part of it, not on top of it.</>
          )}
        </div>
      )}

      {addedOk && !changing && (
        <div style={{ fontSize:'14px', color:T.tx, lineHeight:1.55, marginBottom:'12px' }}>
          An indicative tax provision of <strong>{fmtRand(appliedAmount)}</strong> was added
          {basisNow ? <> on the <strong>{basisLabel(basisNow)}</strong> basis{basisNow === 'sbc' && preview.basis?.table_label ? ` (${preview.basis.table_label})` : ''}</> : ''}
          {decision.by ? ` — confirmed by ${decision.by}` : ''}
          {decision.at ? ` on ${new Date(decision.at).toLocaleDateString('en-ZA')}` : ''}. It is included in the statements as income tax expense and tax payable.
          {basisNow === 'sbc' && <> It is indicative: tax on profit before tax, before assessed losses, non-deductible items, the accelerated allowances and provisional payments.</>}
        </div>
      )}
      {declinedOk && !changing && (
        <div style={{ fontSize:'14px', color:T.tx, lineHeight:1.55, marginBottom:'12px' }}>
          You confirmed no tax provision is to be added: “{decision.reason}”{decision.by ? ` — ${decision.by}` : ''}.
        </div>
      )}

      {showChoice && mode === null && options && (
        <div data-testid="tax-basis-options" style={{ display:'flex', gap:'12px', flexWrap:'wrap', marginBottom:'12px' }}>
          <BasisOption testid="option-standard" title="Standard company rate" subtitle={`${(options.standard.rate * 100).toFixed(2).replace(/\.?0+$/, '')}% of profit before tax`}
            amount={options.standard.amount} actionLabel="Use the standard rate…" busy={busy} btn={btn} onPick={() => pickBasis('standard')} />
          <BasisOption testid="option-sbc" title="Small business corporation" subtitle="Sliding scale, only if the company qualifies"
            amount={sbcOpt.available ? sbcOpt.amount : null} actionLabel="Use the small business corporation basis…"
            disabled={!sbcOpt.available} busy={busy} btn={btn} onPick={() => pickBasis('sbc')}>
            {sbcOpt.available ? (
              <div style={{ fontSize:'12px', color:T.lt, lineHeight:1.5 }}>
                <div data-testid="sbc-workings">{sbcOpt.workings}</div>
                <div>{sbcOpt.table_label}</div>
                <div style={{ color:T.mt, marginTop:'4px' }}>{sbcOpt.note}</div>
              </div>
            ) : (
              <div data-testid="sbc-unavailable" style={{ fontSize:'12px', color:T.lt, lineHeight:1.5 }}>
                {sbcOpt.unavailable_reason}{' '}
                {sbcOpt.admin_path && <a href={sbcOpt.admin_path} style={{ color:T.prg }}>Open the admin screen</a>}
              </div>
            )}
          </BasisOption>
        </div>
      )}

      {showChoice && mode === null && (
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          {!options && <button type="button" style={btn(true)} disabled={busy} onClick={() => { setMode('add'); if (appliedAmount > 0) setAmountInput(String(appliedAmount)) }}>Add a tax provision…</button>}
          <button type="button" style={btn(false)} disabled={busy} onClick={() => setMode('decline')}>Don't add one…</button>
          {changing && <button type="button" style={btn(false)} disabled={busy} onClick={() => setChanging(false)}>Keep my answer</button>}
        </div>
      )}

      {showChoice && mode === 'add' && (
        <div>
          {basis && (
            <div data-testid="chosen-basis" style={{ fontSize:'13px', fontWeight:'700', color:T.tx, marginBottom:'8px' }}>
              Provision on the {basisLabel(basis)} basis{basis === 'sbc' && sbcOpt?.table_label ? ` — ${sbcOpt.table_label}` : ''}
            </div>
          )}
          {basis === 'sbc' && (
            <SbcConfirmation conditions={sbcOpt?.conditions || []} confirmed={sbcConfirmed} onChange={setSbcConfirmed}
              hint={preview.sbc_hint} idPrefix="provision" />
          )}
          <label style={{ fontSize:'13px', color:T.lt, display:'block', margin:'12px 0 6px' }}>
            Provision amount (R) — suggested {fmtRand(basis === 'sbc' ? sbcOpt?.amount : (basis === 'standard' ? options?.standard?.amount : preview.indicative_amount))}; you can change it
            {basis === 'sbc' ? ` (no higher than ${fmtRand(Math.min(preview.pbt, preview.indicative_amount))})` : ''}
          </label>
          <input type="number" min="0" step="0.01" value={amountInput} onChange={e => setAmountInput(e.target.value)}
            aria-label="Provision amount"
            style={{ padding:'8px 10px', borderRadius:'8px', border:`1px solid ${T.bd}`, background:T.s2, color:T.tx, fontSize:'14px', width:'200px' }} />
          {amountErr && <div style={{ fontSize:'12px', color:T.rd, marginTop:'6px' }}>{amountErr}</div>}
          {belowDeferred && (
            <div role="note" style={{ fontSize:'12px', color:T.yl, marginTop:'6px', maxWidth:'560px' }}>
              This is below the deferred tax movement already in your trial balance ({fmtRand(deferredMovement)}), so the tax note would
              show a negative current tax. The amount is the total charge for the year (current + deferred) — check it before confirming.
            </div>
          )}
          <div style={{ display:'flex', gap:'10px', marginTop:'12px', flexWrap:'wrap' }}>
            <button type="button" style={btn(true)} disabled={busy || !!amountErr || sbcBlocked}
              onClick={async () => {
                await onAdd(Math.round(amountNum * 100) / 100, basis ? { basis, sbcConfirmed: basis === 'sbc' && sbcConfirmed } : undefined)
                resetChoice(); setChanging(false)
              }}>
              Confirm — add {amountErr ? 'provision' : fmtRand(amountNum)}
            </button>
            {basis && <button type="button" style={btn(false)} disabled={busy} onClick={resetChoice}>Choose a different basis</button>}
            <button type="button" style={btn(false)} disabled={busy} onClick={resetChoice}>Cancel</button>
          </div>
        </div>
      )}

      {showChoice && mode === 'decline' && (
        <div>
          <label style={{ fontSize:'13px', color:T.lt, display:'block', marginBottom:'6px' }}>
            Why is no tax provision needed? (recorded on the job — e.g. assessed loss brought forward, exempt entity, tax booked in the source system)
          </label>
          <textarea value={reasonInput} onChange={e => setReasonInput(e.target.value)} rows={3}
            placeholder="Why is no tax provision needed?"
            style={{ width:'100%', maxWidth:'560px', padding:'8px 10px', borderRadius:'8px', border:`1px solid ${T.bd}`, background:T.s2, color:T.tx, fontSize:'14px' }} />
          <div style={{ display:'flex', gap:'10px', marginTop:'12px', flexWrap:'wrap' }}>
            <button type="button" style={btn(true)} disabled={busy || !reasonOk}
              onClick={async () => { await onDecline(reasonInput.trim()); setMode(null); setChanging(false) }}>
              Confirm — no provision
            </button>
            <button type="button" style={btn(false)} disabled={busy} onClick={() => setMode(null)}>Cancel</button>
          </div>
        </div>
      )}

      {!showChoice && (
        <div style={{ display:'flex', gap:'10px', flexWrap:'wrap' }}>
          <button type="button" style={btn(false)} disabled={busy} onClick={() => setChanging(true)}>Change my answer</button>
          {addedOk && <button type="button" style={btn(false)} disabled={busy} onClick={onUndo}>Remove the provision</button>}
        </div>
      )}

      {error && <div role="alert" style={{ fontSize:'13px', color:T.rd, marginTop:'10px' }}>{error}</div>}
    </div>
  )
}

// ── Going Concern Override Card ─────────────────────────────────────────────
// Aug 2026: engagement-team judgement call, mirrors TaxRateCard's pattern.
// The computed indicators (shown above in "REVIEW FLAGS") are a defensible
// default, never a replacement for the engagement team's own assessment —
// this card lets them confirm the automatic conclusion or override it in
// either direction, with a documented rationale that gets written directly
// into the Directors' Report and Note 20.
function GoingConcernCard({ clientId, financialYear, computedFlags, onOverrideChanged }) {
  const [status, setStatus]     = useState(null)   // {override, rationale, overridden_by, overridden_at}
  const [loading, setLoading]   = useState(true)
  const [editing, setEditing]   = useState(false)
  const [draftChoice, setDraftChoice] = useState('automatic')  // 'automatic' | 'force_flag' | 'suppress'
  const [rationaleInput, setRationaleInput] = useState('')
  const [saving, setSaving]     = useState(false)

  useEffect(() => {
    if (!clientId || !financialYear) { setLoading(false); return }
    setLoading(true)
    getGoingConcernStatus(clientId, financialYear)
      .then(s => { setStatus(s); setRationaleInput(s.rationale || '') })
      .catch(() => setStatus({ override: null, rationale: null }))
      .finally(() => setLoading(false))
  }, [clientId, financialYear])

  if (loading) return null

  const hasComputedFlags = computedFlags && computedFlags.length > 0
  const currentOverride  = status?.override ?? null  // null = automatic
  const currentEscalates = currentOverride === null ? hasComputedFlags : currentOverride

  const startEditing = () => {
    setDraftChoice(currentOverride === null ? 'automatic' : (currentOverride ? 'force_flag' : 'suppress'))
    setRationaleInput(status?.rationale || '')
    setEditing(true)
  }

  const save = async () => {
    if (draftChoice !== 'automatic' && !rationaleInput.trim()) {
      alert('Please document a rationale before overriding the automatic going-concern assessment — this is what gets written into the Directors\' Report and Note 20.')
      return
    }
    setSaving(true)
    try {
      const overrideValue = draftChoice === 'automatic' ? null : draftChoice === 'force_flag'
      await setGoingConcernOverride(clientId, financialYear, overrideValue, rationaleInput.trim())
      const refreshed = await getGoingConcernStatus(clientId, financialYear)
      setStatus(refreshed)
      setEditing(false)
      if (onOverrideChanged) onOverrideChanged()
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not save the going-concern assessment.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
      padding:'16px 18px', marginBottom:'16px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
        flexWrap:'wrap', gap:'10px' }}>
        <div>
          <div style={{ fontSize:'12px', fontWeight:'700', color:T.mt, marginBottom:'2px' }}>
            GOING CONCERN ASSESSMENT
          </div>
          <div style={{ fontSize:'12px', color:T.mt }}>
            {currentOverride === null
              ? (hasComputedFlags
                  ? 'Automatic — computed indicators will be disclosed as a material uncertainty'
                  : 'Automatic — no computed indicators; entity assessed as a going concern')
              : (currentOverride
                  ? `Engagement team has determined a material uncertainty exists${status?.overridden_by ? ` — set by ${status.overridden_by}` : ''}`
                  : `Engagement team has confirmed no material uncertainty exists${status?.overridden_by ? ` — set by ${status.overridden_by}` : ''}`)}
          </div>
        </div>
        {!editing && (
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <span style={{ fontSize:'11px', fontWeight:700, padding:'4px 10px', borderRadius:'6px',
              background: currentEscalates ? T.rdd : T.gnd,
              color: currentEscalates ? T.rd : T.gn }}>
              {currentEscalates ? 'MATERIAL UNCERTAINTY' : 'NO MATERIAL UNCERTAINTY'}
            </span>
            <button onClick={startEditing}
              style={{ padding:'8px 14px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:'transparent', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
              {currentOverride === null ? 'Override' : 'Change'}
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div style={{ marginTop:'14px', display:'flex', flexDirection:'column', gap:'10px' }}>
          <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
            {[
              { id:'automatic', label:'Automatic (computed indicators decide)' },
              { id:'force_flag', label:'Force: material uncertainty exists' },
              { id:'suppress',   label:'Force: no material uncertainty' },
            ].map(opt => (
              <button key={opt.id} onClick={() => setDraftChoice(opt.id)}
                style={{ padding:'8px 12px', borderRadius:'8px',
                  border: draftChoice === opt.id ? `1px solid ${T.or}` : `1px solid ${T.bd}`,
                  background: draftChoice === opt.id ? T.ord : 'transparent',
                  color: draftChoice === opt.id ? T.or2 : T.mt, fontSize:'12px', cursor:'pointer' }}>
                {opt.label}
              </button>
            ))}
          </div>
          {draftChoice !== 'automatic' && (
            <textarea value={rationaleInput} onChange={e => setRationaleInput(e.target.value)}
              placeholder="Document the reasoning — this text is written directly into the Directors' Report and Note 20 (e.g. 'shareholders have provided a signed letter of continued financial support')."
              rows={3}
              style={{ width:'100%', padding:'10px 12px', background:T.s3, border:`1px solid ${T.bd}`,
                borderRadius:'8px', color:T.tx, fontSize:'13px', boxSizing:'border-box', resize:'vertical' }} />
          )}
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={save} disabled={saving}
              style={{ padding:'8px 14px', borderRadius:'8px', border:'none', background:T.prg,
                color:T.onpri, fontSize:'13px', fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save assessment'}
            </button>
            <button onClick={() => setEditing(false)}
              style={{ padding:'8px 14px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:'transparent', color:T.mt, fontSize:'13px', cursor:'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}


function DeferredTaxCard({ dt }) {
  const [expanded, setExpanded] = useState(false)

  if (!dt || (dt.computed_dtl === 0 && dt.tb_dtl === 0 && dt.ppe_cost === 0)) {
    return (
      <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
        padding:'16px 18px', marginBottom:'16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px' }}>
          <div style={{ fontSize:'12px', fontWeight:'700', color:T.mt }}>DEFERRED TAX</div>
          <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'6px',
            background:T.gnd, color:T.gn, fontWeight:'700' }}>NIL</span>
        </div>
        <div style={{ fontSize:'13px', color:T.mt }}>
          No PPE on balance sheet — deferred tax liability is nil.
        </div>
      </div>
    )
  }

  const tc = DT_TRAFFIC[dt.traffic_light] || DT_TRAFFIC.green
  const varPct = dt.variance_pct ? (dt.variance_pct * 100).toFixed(1) : '0.0'

  return (
    <div style={{ background:tc.bg, border:`1px solid ${tc.border}`,
      borderRadius:'12px', padding:'16px 18px', marginBottom:'16px' }}>

      {/* Header row */}
      <div style={{ display:'flex', justifyContent:'space-between',
        alignItems:'flex-start', marginBottom:'12px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <div style={{ width:'28px', height:'28px', borderRadius:'8px',
            background:alpha(tc.text, 0.125), display:'flex', alignItems:'center',
            justifyContent:'center', color:tc.text }}>
            <tc.icon size={16} strokeWidth={2} />
          </div>
          <div>
            <div style={{ fontSize:'13px', fontWeight:'700', color:T.tx }}>
              Deferred Tax — SARS IT-180
            </div>
            <div style={{ fontSize:'10px', color:T.mt, marginTop:'1px' }}>
              Temporary difference approach · SA corporate rate {
                dt.tax_rate != null ? `${(dt.tax_rate * 100).toFixed(0)}%` : '27%'
              }
            </div>
          </div>
        </div>
        <span style={{ fontSize:'10px', fontWeight:'800', padding:'3px 10px',
          borderRadius:'6px', background:alpha(tc.text, 0.125), color:tc.text }}>
          {tc.label}
        </span>
      </div>

      {/* Computed vs TB comparison */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr',
        gap:'8px', marginBottom:'12px' }}>
        {[
          { label:'Computed DTL',  value:dt.computed_dtl, color:T.tx },
          { label:'Trial Balance', value:dt.tb_dtl,       color:T.tx },
          { label:'Variance',      value:dt.variance_dtl, color: dt.traffic_light === 'green' ? T.gn : dt.traffic_light === 'amber' ? T.yl : T.rd },
        ].map((item, i) => (
          <div key={i} style={{ background:T.bg, borderRadius:'8px',
            padding:'10px 12px' }}>
            <div style={{ fontSize:'9px', color:T.mt, letterSpacing:'0.5px',
              marginBottom:'4px', fontWeight:'600' }}>
              {item.label.toUpperCase()}
            </div>
            <div style={{ fontSize:'16px', fontWeight:'800', color:item.color,
              fontFamily:"'DM Mono',monospace" }}>
              {fmtR(item.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{ fontSize:'13px', color:T.tx, lineHeight:'1.6',
        marginBottom:'10px' }}>
        {dt.summary}
      </div>

      {/* Jul 2026 bug fix: honest limitation when no date of incorporation
          is on record, so the per-asset cross-check can't independently
          estimate divergence from IFRS — see deferred_tax.py's
          period_caveat / age_confidence. */}
      {dt.period_caveat && (
        <div style={{ fontSize:'12px', color:T.yl, lineHeight:'1.6', marginBottom:'10px',
          background:alpha(T.am, 0.08), border:`1px solid ${alpha(T.am, 0.25)}`,
          borderRadius:'6px', padding:'8px 10px' }}>
          <Icon name="alert" /> {dt.period_caveat}
        </div>
      )}

      {/* Effective tax rate context if available */}
      {dt.pbt > 0 && (
        <div style={{ fontSize:'12px', color:T.mt, marginBottom:'10px' }}>
          PPE cost R{(dt.ppe_cost||0).toLocaleString()} · Carrying amount R{(dt.ppe_carrying||0).toLocaleString()} · Total temporary differences R{(dt.total_temp_diff||0).toLocaleString()}
        </div>
      )}

      {/* Expandable workings */}
      {dt.workings && dt.workings.length > 0 && (
        <>
          <button
            onClick={() => setExpanded(v => !v)}
            style={{ display:'flex', alignItems:'center', gap:'4px',
              background:'none', border:`1px solid ${tc.border}`, borderRadius:'6px',
              padding:'5px 12px', cursor:'pointer', fontSize:'12px',
              color:tc.text, fontFamily:"'Outfit',sans-serif", fontWeight:'600',
              transition:'all 0.2s' }}>
            <span style={{ transition:'transform 0.2s',
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
            {expanded ? 'Hide' : 'Show'} SARS IT-180 workings
            ({dt.workings.length} asset class{dt.workings.length !== 1 ? 'es' : ''})
          </button>

          {expanded && (
            <div style={{ marginTop:'12px' }}>
              {/* Column headers */}
              <div style={{ display:'grid',
                gridTemplateColumns:'1fr 90px 90px 90px 90px',
                padding:'6px 8px', fontSize:'9px', fontWeight:'700',
                color:T.mt, letterSpacing:'1px',
                borderBottom:`1px solid ${T.bd}` }}>
                <div>ASSET CLASS</div>
                <div style={{textAlign:'right'}}>IFRS NBV</div>
                <div style={{textAlign:'right'}}>SARS BASE</div>
                <div style={{textAlign:'right'}}>TEMP DIFF</div>
                <div style={{textAlign:'right'}}>DT LIABILITY</div>
              </div>

              {dt.workings.map((w, i) => (
                <div key={i} style={{ display:'grid',
                  gridTemplateColumns:'1fr 90px 90px 90px 90px',
                  padding:'7px 8px',
                  borderBottom:`1px solid ${alpha(T.bd, 0.5)}`,
                  background: i % 2 === 0 ? 'transparent' : alpha(T.tx, 0.02),
                  alignItems:'center' }}>
                  <div>
                    <div style={{ fontSize:'12px', color:T.tx }}>{w.asset_class}</div>
                    {w.sars_rate > 0 && (
                      <div style={{ fontSize:'9px', color:T.mt }}>
                        {(w.sars_rate * 100).toFixed(1)}%/yr IT-180
                      </div>
                    )}
                    {w.note && w.sars_rate === 0 && (
                      <div style={{ fontSize:'9px', color:T.mt }}>{w.note}</div>
                    )}
                  </div>
                  <div style={{ textAlign:'right', fontSize:'12px', color:T.tx,
                    fontFamily:"'DM Mono',monospace" }}>
                    {fmtR(w.ifrs_carrying)}
                  </div>
                  <div style={{ textAlign:'right', fontSize:'12px', color:T.mt,
                    fontFamily:"'DM Mono',monospace" }}>
                    {fmtR(w.tax_base)}
                  </div>
                  <div style={{ textAlign:'right', fontSize:'12px',
                    color: (w.temp_diff||0) > 0 ? T.yl : T.mt,
                    fontFamily:"'DM Mono',monospace" }}>
                    {fmtR(w.temp_diff)}
                  </div>
                  <div style={{ textAlign:'right', fontSize:'12px',
                    color: (w.dt_liability||0) > 0 ? tc.text : T.mt,
                    fontWeight:'700', fontFamily:"'DM Mono',monospace" }}>
                    {fmtR(w.dt_liability)}
                  </div>
                </div>
              ))}

              {/* Totals row */}
              <div style={{ display:'grid',
                gridTemplateColumns:'1fr 90px 90px 90px 90px',
                padding:'8px 8px',
                background:T.bg, borderTop:`1.5px solid ${T.bd}`,
                alignItems:'center' }}>
                <div style={{ fontSize:'12px', fontWeight:'700', color:T.or }}>TOTAL</div>
                <div style={{ textAlign:'right', fontSize:'12px', color:T.tx,
                  fontWeight:'700', fontFamily:"'DM Mono',monospace" }}>
                  {fmtR(dt.ppe_carrying)}
                </div>
                <div style={{ textAlign:'right', fontSize:'12px', color:T.mt,
                  fontFamily:"'DM Mono',monospace" }}>—</div>
                <div style={{ textAlign:'right', fontSize:'12px', color:T.yl,
                  fontWeight:'700', fontFamily:"'DM Mono',monospace" }}>
                  {fmtR(dt.total_temp_diff)}
                </div>
                <div style={{ textAlign:'right', fontSize:'14px', color:tc.text,
                  fontWeight:'800', fontFamily:"'DM Mono',monospace" }}>
                  {fmtR(dt.computed_dtl)}
                </div>
              </div>

              {/* SARS rate reference */}
              <div style={{ marginTop:'10px', padding:'10px 12px',
                background:alpha(T.bl, 0.06),
                border:`1px solid ${alpha(T.bl, 0.15)}`,
                borderRadius:'8px', fontSize:'10px', color:T.bl,
                lineHeight:'1.7' }}>
                <strong>SARS IT-180 rates used:</strong> Computer equipment 33.3%/yr (3 yrs) · Motor vehicles 20%/yr (5 yrs) · Furniture &amp; fittings 16.7%/yr (6 yrs) · Plant &amp; machinery 20%/yr (5 yrs) · Buildings 4%/yr (25 yrs) · Leasehold improvements 10%/yr (10 yrs) · Land: no allowance
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN REVIEW COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function Review() {
  const navigate  = useNavigate()
  const location  = useLocation()
  const { firm }  = useAuth()

  const client        = location.state?.client
  const baseMappingsIn = location.state?.mappings      || {}
  const rawAccounts    = location.state?.accounts      || []
  const prior_figures        = location.state?.prior_figures        || {}
  const first_year           = location.state?.first_year           || false
  const prior_not_available  = location.state?.prior_not_available  || false
  const notes         = location.state?.notes         || {}
  // FIX (Sep 2026 — bank-statement flow now routes through Review before
  // Statements, see BankStatementBalanceSheet.jsx/Statements.jsx): carried
  // through so the "Back" button below can return to the balance-sheet
  // screen (with figures intact) instead of Notes, which doesn't exist in
  // this flow, and so handleProceed can forward it on to Statements exactly
  // as BankStatementBalanceSheet.jsx used to when it navigated there directly.
  const bank_statement_source = location.state?.bank_statement_source || false
  // FIX (Sep 2026 — "Back" wipeout on the bank-statement flow, see
  // BankStatement.jsx for the full writeup): opaque payload this page
  // neither reads nor modifies, only round-trips between
  // BankStatementBalanceSheet.jsx and Statements.jsx so BankStatement.jsx
  // can restore itself if the preparer goes all the way back.
  const bs_snapshot = location.state?.bs_snapshot || null
  const notes_summary = location.state?.notes_summary || {}
  const afsJobId      = location.state?.afsJobId      || null
  const financial_year = location.state?.financial_year ||
                         sessionStorage.getItem('kailux:financial_year') || null
  // Phase 1 (Jul 2026): explicit period from the free date-range picker,
  // forwarded the same way afsJobId/financial_year already are (see
  // kailux-fixes-log.md §3.3 for why this pattern exists).
  const year_end     = location.state?.year_end ||
                        sessionStorage.getItem('kailux:year_end') || ''
  const period_start = location.state?.period_start ||
                        sessionStorage.getItem('kailux:period_start_display') || ''
  // FIX (kailux-fixes-log.md — "Back to Mapping loses all trial-balance
  // progress"): carried through so a Review → Notes → Mapping round trip
  // still lets TrialBalance.jsx's resume-hydration effect restore the
  // actually-selected period, not just a fresh Notes → Mapping trip.
  const period_start_iso = location.state?.period_start_iso || null
  const period_end_iso   = location.state?.period_end_iso   || null

  const stateFigures = location.state?.figures || null
  // 29 Sep 2026 (§88) — an indicative tax provision the preparer explicitly
  // confirmed. It is carried through the flow as two real accounts (see
  // utils/taxProvision.js), so on re-entry (Back from Statements/Notes) it is
  // recognised, stripped to recover the pre-provision base, and re-applied below
  // — never applied twice. `provisionAmount` is the amount currently in force.
  const carriedProvision = extractTaxProvision(rawAccounts)
  const baseAccounts     = carriedProvision.accounts
  const baseFigures      = (stateFigures ? withoutTaxProvisionFigures(stateFigures, carriedProvision.amount) : null)
                           || buildFiguresFromAccounts(baseAccounts, baseMappingsIn)
  const [provisionAmount, setProvisionAmount] = useState(carriedProvision.amount || 0)
  // Net deferred tax charge/(credit) already in the trial balance: the year's change
  // in the deferred tax liability less the change in the deferred tax asset — the same
  // arithmetic the statements use to split the tax note into current and deferred.
  const num = (o, k) => parseFloat((o || {})[k]) || 0
  const deferredTaxMovement = first_year
    ? num(baseFigures, 'Deferred Tax Liability') - num(baseFigures, 'Deferred Tax Asset')
    : (num(baseFigures, 'Deferred Tax Liability') - num(prior_figures, 'Deferred Tax Liability'))
      - (num(baseFigures, 'Deferred Tax Asset') - num(prior_figures, 'Deferred Tax Asset'))
  const adjusted = provisionAmount > 0
    ? applyTaxProvision({ accounts: baseAccounts, mappings: baseMappingsIn, figures: baseFigures }, provisionAmount)
    : null
  const accounts = adjusted ? adjusted.accounts : baseAccounts
  const mappings = adjusted ? adjusted.mappings : baseMappingsIn
  const figures  = adjusted ? adjusted.figures  : baseFigures
  // Shared with TrialBalance/Statements/Notes and the backend's
  // aggregate_figures_from_accounts() — see utils/accountMapping.js.
  const isUnmappedAcc = (m, name) => isUnmappedAccount(m, name)
  const unmappedAccounts = accounts.filter(acc => isUnmappedAcc(mappings[acc.code], acc.name))
  // Phase 4 — input-side anomaly detection: distinct from isUnmappedAcc.
  // These accounts DO have a valid category and their amounts must still
  // count in figures below — the gate here is about whether a human has
  // actually looked at a low-confidence AI suggestion, not whether the
  // account has a category at all.
  const isUnconfirmedLowConfidence = (m) =>
    !!m && m.source !== 'manual' && m.source !== 'unassigned' && m.confidence < 70 && !m.confirmed
  const unconfirmedLowConfidenceAccounts = accounts.filter(acc => isUnconfirmedLowConfidence(mappings[acc.code]))

  // Build sub_accounts to pass for deferred tax per-asset computation
  const sub_accounts = (() => {
    if (!accounts.length || !Object.keys(mappings).length) return {}
    const result = {}
    accounts.forEach(acc => {
      const m = mappings[String(acc.code)]
      if (!m) return
      const cat = m.ifrs_category
      if (!cat) return
      if (!result[cat]) result[cat] = []
      result[cat].push({
        account_code: String(acc.code),
        account_name: acc.name || '',
        debit:        parseFloat(acc.debit  || 0),
        credit:       parseFloat(acc.credit || 0),
        net:          parseFloat(acc.debit  || 0) - parseFloat(acc.credit || 0),
      })
    })
    return result
  })()

  const deriveYear = () => {
    const now = new Date()
    const monthNames = ['January','February','March','April','May','June',
      'July','August','September','October','November','December']
    const yearEndMonth = client?.year_end_month || 'February'
    const yearEndIdx   = monthNames.indexOf(yearEndMonth)
    if (yearEndIdx === -1) return String(now.getFullYear())
    return now.getMonth() >= yearEndIdx
      ? String(now.getFullYear())
      : String(now.getFullYear() - 1)
  }

  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState('')
  const [ratios,       setRatios]       = useState(null)
  const [priorRatios,  setPriorRatios]  = useState(null)
  const [observations, setObservations] = useState([])
  const [flags,        setFlags]        = useState([])
  const [deferredTax,  setDeferredTax]  = useState(null)   // Phase R3
  // FIX (kailux-fixes-log.md — "Resumed review checklist doesn't restore"):
  // this used to always initialise every item to false, ignoring any
  // previously-confirmed checklist AFSHistory's Resume flow now forwards
  // via location.state.disclosure_checklist (see resume_job_progress /
  // handleResume). A resumed review-stage job forced re-ticking all 7
  // items from scratch even though they'd genuinely already been confirmed
  // in a prior session. Server-side validation in /api/afs/generate is
  // unaffected either way — this only restores the local UI state.
  const confirmedChecklist = location.state?.disclosure_checklist || {}
  const [checklist,    setChecklist]    = useState(
    Object.fromEntries(CHECKLIST_ITEMS.map(item =>
      [item.id, !!confirmedChecklist[item.id]]))
  )

  const allChecked    = Object.values(checklist).every(Boolean)
  const checkedCount  = Object.values(checklist).filter(Boolean).length
  const totalItems    = CHECKLIST_ITEMS.length

  useEffect(() => {
    if (first_year) {
      setChecklist(prev => ({ ...prev, comparatives: true }))
    }
  }, [first_year])

  useEffect(() => {
    if (!client?.id || !firm?.id) return
    fetchReview()
  }, [client?.id, firm?.id])

  const fetchReview = async () => {
    setLoading(true); setError('')
    try {
      const { data } = await generateReview({
        client_id:       client.id,
        firm_id:         firm.id,
        figures,
        prior_figures,
        first_year,
        company:         client.name  || '',
        year_end:        year_end || `${client.year_end_month || 'February'} ${deriveYear()}`,
        period_start:    period_start || undefined,
        date_of_incorporation: client.date_of_incorporation || undefined,
        entity_type:     client.entity_type     || 'Pty Ltd',
        engagement_type: client.engagement_type || firm.engagement_type   || 'compilation',
        sub_accounts,   // Phase R3: pass for per-asset DT computation
        financial_year,  // Phase 10: lets the backend resolve THIS job's
                         // confirmed tax rate rather than always the
                         // hardcoded default — see TaxRateCard below.
      })
      setRatios(data.ratios)
      setPriorRatios(data.prior_ratios || {})
      setObservations(data.observations || [])
      setFlags(data.flags || [])
      setDeferredTax(data.deferred_tax || null)   // Phase R3
    } catch(err) {
      setError('Could not load review data. Check Railway logs.')
    } finally {
      setLoading(false)
    }
  }

  // ── Tax provision (29 Sep 2026, §88) ──────────────────────────────────────
  const [taxPreview,  setTaxPreview]  = useState(null)   // server's view of the gap, from the PRE-provision figures
  const [taxDecision, setTaxDecision] = useState(null)   // the preparer's recorded answer, if any
  const [taxBusy,     setTaxBusy]     = useState(false)
  const [taxError,    setTaxError]    = useState('')
  const [rateVersion, setRateVersion] = useState(0)      // bumped when the tax rate is confirmed → refresh the suggestion
  const taxYearEnd = year_end || `${client?.year_end_month || 'February'} ${deriveYear()}`

  useEffect(() => {
    if (!client?.id || !financial_year) return
    let cancelled = false
    previewTaxProvision(client.id, financial_year, baseFigures, taxYearEnd)
      .then(d => {
        if (cancelled) return
        setTaxPreview(d)
        setTaxDecision(d.decision || null)
        // Resume path: the preparer already confirmed adding a provision, but the
        // state we arrived with doesn't carry it (a rebuilt bank-statement balance
        // sheet, a resumed job). Re-apply THEIR confirmed amount — never invent
        // one — and only while it is still valid against today's profit.
        const dec = d.decision
        if (d.applies && dec?.decision === 'added' && dec.amount > 0 && dec.amount <= d.pbt + 0.01) {
          setProvisionAmount(prev => prev > 0 ? prev : dec.amount)
        }
      })
      // If the check itself cannot run we do not block the preparer — the
      // validation gate on the Statements page still raises the same warning.
      .catch(() => { if (!cancelled) setTaxPreview(null) })
    return () => { cancelled = true }
  }, [client?.id, financial_year, rateVersion])

  // The AI review reads the figures, so re-run it when a provision is added/removed.
  const firstProvisionRun = useRef(true)
  useEffect(() => {
    if (firstProvisionRun.current) { firstProvisionRun.current = false; return }
    if (client?.id && firm?.id) fetchReview()
  }, [provisionAmount])

  const saveTaxDecision = async (payload) => {
    setTaxBusy(true); setTaxError('')
    try {
      return await saveTaxProvisionDecision({
        clientId: client.id, financialYear: financial_year, figures: baseFigures, yearEnd: taxYearEnd, ...payload,
      })
    } catch (err) {
      setTaxError(err?.response?.data?.detail || 'Could not save your tax provision answer. Please try again.')
      return null
    } finally { setTaxBusy(false) }
  }
  const handleTaxAdd = async (amount, opts) => {
    // opts = { basis, sbcConfirmed } from the two-option card; undefined = the pre-§108 single flow.
    const rec = await saveTaxDecision({ decision: 'added', amount, ...(opts || {}) })
    if (!rec) return
    setTaxDecision({ decision: 'added', amount: rec.amount, by: rec.by, at: rec.at, basis: rec.basis || null })
    setProvisionAmount(rec.amount)
    if (rec.basis) setRateVersion(v => v + 1)   // the engagement basis changed: refresh the preview and the Tax basis card
  }
  const handleTaxDecline = async (reason) => {
    const rec = await saveTaxDecision({ decision: 'declined', reason })
    if (!rec) return
    setTaxDecision({ decision: 'declined', reason: rec.reason, by: rec.by, at: rec.at })
    setProvisionAmount(0)   // a provision added earlier is withdrawn by choosing "don't add"
  }
  const handleTaxUndo = async () => {
    const rec = await saveTaxDecision({ decision: 'cleared' })
    if (!rec) return
    setTaxDecision(null)
    setProvisionAmount(0)
  }
  const taxResolved = taxDecision?.decision === 'declined' || (taxDecision?.decision === 'added' && provisionAmount > 0)
  const taxPending  = !!taxPreview?.applies && !taxResolved

  const toggleCheck = (id) => {
    setChecklist(prev => ({ ...prev, [id]: !prev[id] }))
  }

  const [checklistSaveError, setChecklistSaveError] = useState('')

  const handleProceed = async () => {
    if (taxPending) {
      window.alert(
        'Your figures show a profit but no income tax. Please choose whether to add an ' +
        'indicative tax provision (see the income tax card above) before continuing.'
      )
      return
    }
    if (unmappedAccounts.length > 0) {
      window.alert(
        `${unmappedAccounts.length} account(s) have no confirmed IFRS category mapping ` +
        `(${unmappedAccounts.map(a => a.name).join(', ')}). They will not appear anywhere ` +
        `in the financial statements. Go back and map them before generating.`
      )
      return
    }
    if (unconfirmedLowConfidenceAccounts.length > 0) {
      window.alert(
        `${unconfirmedLowConfidenceAccounts.length} account(s) have a low-confidence AI-suggested ` +
        `mapping that hasn't been reviewed (${unconfirmedLowConfidenceAccounts.map(a => a.name).join(', ')}). ` +
        `Go back to Trial Balance and confirm each one before generating.`
      )
      return
    }
    // Persist the checklist server-side BEFORE navigating — /api/afs/generate
    // independently checks this record rather than trusting anything sent
    // from the frontend, so this call has to succeed (not just the local
    // `allChecked` state) or generation will be rejected downstream anyway.
    setChecklistSaveError('')
    try {
      await confirmDisclosureChecklist({
        client_id: client?.id,
        financial_year,
        checklist,
      })
    } catch (err) {
      setChecklistSaveError(
        'Could not save the disclosure checklist confirmations. Please try again before proceeding.'
      )
      return
    }
    if (afsJobId) {
      try {
        await saveJobProgress(afsJobId, { stage: 'review' })
      } catch (err) {
        console.error('Autosave failed (non-fatal):', err)
      }
    }
    // Review engagements (Oct 2026): the review file must be completed and approved
    // before a reviewed AFS can be generated, so route through it. Same state onward.
    const isReviewEngagement = (client?.engagement_type || firm?.engagement_type) === 'review'
    navigate(isReviewEngagement ? '/review-file' : '/statements', {
      state: {
        client, mappings, accounts,
        figures, prior_figures, first_year, prior_not_available,
        financial_year, year_end, period_start,
        notes, notes_summary,
        afsJobId,
        // FIX (Sep 2026 — bank-statement flow now routes through here
        // instead of straight to Statements): forward these through exactly
        // as BankStatementBalanceSheet.jsx used to when it navigated to
        // /statements directly, so Statements.jsx's own bank_statement_source
        // handling (API calls, Back-button target) still works unchanged.
        source: bank_statement_source ? 'bank_statement' : undefined,
        bank_statement_source,
        bs_snapshot,
      }
    })
  }

  const handleBack = () => {
    // FIX (Sep 2026 — bank-statement flow now routes through Additional
    // Documents and Notes too, not just Review): this flow DOES have a
    // Notes stage now (see AdditionalDocuments.jsx/Notes.jsx/
    // BankStatementBalanceSheet.jsx), so Back goes there like every other
    // flow — the branch here just needs to forward `source`/
    // `bank_statement_source` so Notes' own Back button still knows to
    // return to the balance-sheet screen rather than /trial-balance.
    // bs_snapshot is carried through too (see BankStatement.jsx/
    // BankStatementBalanceSheet.jsx) so the "Back to transactions" wipeout
    // fix keeps working now that Notes sits in between here and there.
    navigate('/notes', {
      state: {
        client, mappings, accounts, figures, prior_figures, first_year, prior_not_available,
        financial_year, year_end, period_start,
        period_start_iso, period_end_iso,
        notes, afsJobId,
        source: bank_statement_source ? 'bank_statement' : undefined,
        bank_statement_source,
        bs_snapshot,
      }
    })
  }

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', maxWidth:'480px' }}>
        <div style={{ width:'64px', height:'64px',
          background:T.prg, color:T.onpri,
          borderRadius:'16px', margin:'0 auto 24px',
          display:'flex', alignItems:'center', justifyContent:'center', fontSize:'28px' }}>
          <Icon name="chart" />
        </div>
        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
          fontWeight:'800', marginBottom:'8px' }}>
          Generating Expert Review
        </div>
        <div style={{ fontSize:'14px', color:T.mt, marginBottom:'32px', lineHeight:'1.7' }}>
          Calculating ratios, deferred tax, and drafting observations
        </div>
        <div style={{ display:'flex', justifyContent:'center', gap:'8px' }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width:'10px', height:'10px', borderRadius:'50%',
              background:T.or, animation:`bounce 1.2s ${i*0.2}s infinite` }}/>
          ))}
        </div>
      </div>
      <style>{`@keyframes bounce{0%,60%,100%{transform:translateY(0)}30%{transform:translateY(-10px)}}`}</style>
    </div>
  )

  // ── Error screen ───────────────────────────────────────────────────────────
  if (error) return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ textAlign:'center', maxWidth:'400px' }}>
        <div style={{ fontSize:'48px', marginBottom:'16px' }}><Icon name="alert" strokeWidth={1.5} style={{ color: T.yl }} /></div>
        <div style={{ fontSize:'16px', fontWeight:'700', marginBottom:'8px' }}>Review could not load</div>
        <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px' }}>{error}</div>
        <div style={{ display:'flex', gap:'8px', justifyContent:'center' }}>
          <button onClick={fetchReview}
            style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px',
              fontWeight:'700', cursor:'pointer' }}>
            Try again
          </button>
          <button onClick={handleProceed}
            style={{ padding:'10px 22px', borderRadius:'8px',
              border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
            Skip review →
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx }}>

      {/* ── TOP BAR ── */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
        padding:'0 28px', height:'62px', display:'flex', alignItems:'center',
        justifyContent:'space-between', position:'sticky', top:0, zIndex:50, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <BackButton onClick={handleBack} label="Back to Notes"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          {client && (
            <span style={{ fontSize:'13px', color:T.mt,
              borderLeft:`1px solid ${T.bd}`, paddingLeft:'12px' }}>
              {client.name}
            </span>
          )}
          <span style={{ fontSize:'10px', padding:'2px 10px', borderRadius:'6px',
            background:T.ord, color:T.or, fontWeight:'700' }}>
            Expert Review
          </span>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <div style={{ fontSize:'13px', color: allChecked ? T.gn : T.mt }}>
            {allChecked
              ? <><Icon name="check" /> All checks complete</>
              : `${checkedCount} of ${totalItems} checks completed`}
          </div>
          <button
            onClick={handleProceed}
            disabled={!allChecked || unmappedAccounts.length > 0 || unconfirmedLowConfidenceAccounts.length > 0}
            title={unmappedAccounts.length > 0
              ? `${unmappedAccounts.length} account(s) unmapped — resolve before generating`
              : unconfirmedLowConfidenceAccounts.length > 0
              ? `${unconfirmedLowConfidenceAccounts.length} low-confidence mapping(s) need confirmation — go back to Trial Balance`
              : (!allChecked ? 'Complete all disclosure checks to proceed' : '')}
            style={{ padding:'8px 20px', borderRadius:'8px', border:'none',
              background: (allChecked && unmappedAccounts.length === 0 && unconfirmedLowConfidenceAccounts.length === 0)
                ? T.prg
                : T.s2,
              color: (allChecked && unmappedAccounts.length === 0 && unconfirmedLowConfidenceAccounts.length === 0) ? T.onpri : T.mt,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px',
              fontWeight:'700', cursor: (allChecked && unmappedAccounts.length === 0 && unconfirmedLowConfidenceAccounts.length === 0) ? 'pointer' : 'not-allowed',
              opacity: (allChecked && unmappedAccounts.length === 0 && unconfirmedLowConfidenceAccounts.length === 0) ? 1 : 0.6, transition:'all 0.3s' }}>
            Generate PDF →
          </button>
        </div>
      </div>

      <div style={{ padding:'28px 32px', maxWidth:'1200px', margin:'0 auto' }}>

        {/* ── UNMAPPED ACCOUNTS WARNING ── */}
        {unmappedAccounts.length > 0 && (
          <div style={{ background:T.rdd, border:`1px solid ${T.rd}`,
            borderRadius:'12px', padding:'16px 20px', marginBottom:'16px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:T.rd,
              letterSpacing:'1.5px', marginBottom:'6px' }}>
              <Icon name="alert" /> {unmappedAccounts.length} ACCOUNT(S) UNMAPPED — WILL NOT APPEAR IN STATEMENTS
            </div>
            <div style={{ fontSize:'14px', color:T.rd }}>
              {unmappedAccounts.map(a => a.name).join(', ')} — go back to Trial Balance and
              assign a category to each before generating, or these figures will be silently
              excluded from every statement and note.
            </div>
          </div>
        )}

        {/* ── UNCONFIRMED LOW-CONFIDENCE MAPPINGS WARNING ── */}
        {unconfirmedLowConfidenceAccounts.length > 0 && (
          <div style={{ background:alpha(T.or, 0.08), border:`1px solid ${T.or}`,
            borderRadius:'12px', padding:'16px 20px', marginBottom:'16px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:T.or,
              letterSpacing:'1.5px', marginBottom:'6px' }}>
              <Icon name="alert" /> {unconfirmedLowConfidenceAccounts.length} LOW-CONFIDENCE MAPPING(S) NEED CONFIRMATION
            </div>
            <div style={{ fontSize:'14px', color:T.tx }}>
              {unconfirmedLowConfidenceAccounts.map(a => a.name).join(', ')} — these accounts
              have a valid category, but the AI's confidence was low and nobody has confirmed
              it's correct yet. Go back to Trial Balance and confirm each one before generating.
            </div>
          </div>
        )}

        {/* ── GOING CONCERN FLAGS ── */}
        {flags.length > 0 && (
          <div style={{ background:T.rdd, border:`1px solid ${alpha(T.rd, 0.25)}`,
            borderRadius:'12px', padding:'16px 20px', marginBottom:'24px' }}>
            <div style={{ fontSize:'12px', fontWeight:'700', color:T.rd,
              letterSpacing:'1.5px', marginBottom:'10px' }}>
              <Icon name="alert" /> REVIEW FLAGS
            </div>
            {flags.map((flag, i) => (
              <div key={i} style={{ fontSize:'14px',
                color: flag.type === 'danger' ? T.rd : T.yl,
                padding:'4px 0', display:'flex', gap:'8px', alignItems:'flex-start' }}>
                <span style={{ color: flag.type === 'danger' ? T.rd : T.yl, flexShrink:0 }}>•</span>
                {flag.message || flag}
              </div>
            ))}
          </div>
        )}

        {/* Aug 2026: the computed flags above are a defensible default, not
            a replacement for the engagement team's own judgement — this
            card lets them confirm or override the going-concern conclusion
            with a documented rationale. */}
        <TaxProvisionCard
          preview={taxPreview}
          decision={taxDecision}
          appliedAmount={provisionAmount}
          busy={taxBusy}
          error={taxError}
          onAdd={handleTaxAdd}
          onDecline={handleTaxDecline}
          onUndo={handleTaxUndo}
          deferredMovement={deferredTaxMovement}
        />

        <GoingConcernCard
          clientId={client?.id}
          financialYear={financial_year}
          computedFlags={flags}
          onOverrideChanged={fetchReview}
        />

        <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:'24px' }}>

          {/* ── LEFT COLUMN ── */}
          <div>

            {/* Ratio grid */}
            <div style={{ marginBottom:'24px' }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px',
                fontWeight:'800', marginBottom:'4px' }}>
                Financial Ratios
              </div>
              <div style={{ fontSize:'13px', color:T.mt, marginBottom:'16px' }}>
                {!first_year && Object.keys(priorRatios || {}).length > 0
                  ? 'Current year with prior year comparison'
                  : 'Current year — no prior year for comparison'}
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                {RATIO_META.map(meta => {
                  const value      = ratios?.[meta.key] ?? null
                  const priorValue = priorRatios?.[meta.key] ?? null
                  const band       = meta.benchmark(value)
                  const bc         = BENCH_COLORS[band]
                  const trend      = RATIO_TREND_DIRECTION[meta.key] !== null
                    ? trendArrow(value, priorValue, RATIO_TREND_DIRECTION[meta.key])
                    : null

                  return (
                    <div key={meta.key}
                      style={{ background:bc.bg, border:`1px solid ${bc.border}`,
                        borderRadius:'12px', padding:'16px 18px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between',
                        alignItems:'flex-start', marginBottom:'8px' }}>
                        <div style={{ fontSize:'12px', color:T.mt, fontWeight:'600',
                          lineHeight:'1.4', flex:1, paddingRight:'8px' }}>
                          {meta.label}
                        </div>
                        {trend && (
                          <span style={{ fontSize:'16px', color:trend.color,
                            fontWeight:'800', flexShrink:0 }}>
                            {trend.symbol}
                          </span>
                        )}
                      </div>

                      <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'28px',
                        fontWeight:'800', color:bc.text, letterSpacing:'-0.5px',
                        marginBottom:'4px' }}>
                        {meta.format(value)}
                      </div>

                      {priorValue !== null && priorValue !== undefined && (
                        <div style={{ fontSize:'12px', color:T.mt, marginBottom:'6px' }}>
                          Prior: {meta.format(priorValue)}
                        </div>
                      )}

                      <div style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                        <div style={{ width:'5px', height:'5px', borderRadius:'50%',
                          background:bc.dot, flexShrink:0 }}/>
                        <span style={{ fontSize:'10px', color:bc.text, fontWeight:'600' }}>
                          {meta.benchmarkLabel(value)}
                        </span>
                      </div>

                      <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px' }}>
                        {meta.desc}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Phase R3: Deferred Tax Card */}
            <div style={{ marginBottom:'24px' }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'16px',
                fontWeight:'800', marginBottom:'12px' }}>
                Deferred Tax Verification
              </div>
              {/* Phase 10 (Jul 2026): confirm/override the effective tax
                  rate for THIS engagement before relying on the deferred
                  tax numbers below — matters most for a genuinely
                  historical AFS where today's rate may not be correct. */}
              <TaxRateCard
                clientId={client?.id}
                financialYear={financial_year}
                yearEnd={year_end || `${client?.year_end_month || 'February'} ${deriveYear()}`}
                onConfirmed={() => { fetchReview(); setRateVersion(v => v + 1) }}
              />
              {/* §108: the engagement's tax basis (standard rate | small business corporation), confirmed
                  by the preparer — KaiLux never assumes it. Later features read this one record. */}
              <TaxBasisCard
                clientId={client?.id}
                financialYear={financial_year}
                yearEnd={taxYearEnd}
                refreshKey={rateVersion}
                onChanged={() => setRateVersion(v => v + 1)}
              />
              <DeferredTaxCard dt={deferredTax} />
            </div>

            {/* AI Observations */}
            {observations.length > 0 && (
              <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
                borderRadius:'12px', padding:'20px 22px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px' }}>
                  <div style={{ width:'28px', height:'28px', borderRadius:'8px',
                    background:T.ord, display:'flex', alignItems:'center',
                    justifyContent:'center', fontSize:'14px' }}><Icon name="ai" /></div>
                  <div>
                    <div style={{ fontSize:'14px', fontWeight:'700' }}>AI Observations</div>
                    <div style={{ fontSize:'10px', color:T.mt }}>
                      Generated by KaiLux — review before signing off
                    </div>
                  </div>
                </div>
                {observations.map((obs, i) => (
                  <div key={i}
                    style={{ display:'flex', gap:'12px', alignItems:'flex-start',
                      padding:'10px 0',
                      borderBottom: i < observations.length - 1
                        ? `1px solid ${T.bd}` : 'none' }}>
                    <div style={{ width:'22px', height:'22px', borderRadius:'6px',
                      background:T.ord, display:'flex', alignItems:'center',
                      justifyContent:'center', fontSize:'10px', fontWeight:'800',
                      color:T.or, flexShrink:0, marginTop:'1px' }}>
                      {i + 1}
                    </div>
                    <div style={{ fontSize:'14px', color:T.tx, lineHeight:'1.6' }}>
                      {obs}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN — Checklist ── */}
          <div>
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', overflow:'hidden',
              position:'sticky', top:'78px' }}>

              <div style={{ padding:'18px 20px', borderBottom:`1px solid ${T.bd}` }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'15px',
                  fontWeight:'800', marginBottom:'4px' }}>
                  Disclosure Checklist
                </div>
                <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.5' }}>
                  Tick each item to unlock PDF generation.
                  You take professional responsibility for these confirmations.
                </div>

                <div style={{ marginTop:'12px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between',
                    fontSize:'10px', color:T.mt, marginBottom:'5px' }}>
                    <span>Progress</span>
                    <span style={{ color: allChecked ? T.gn : T.or, fontWeight:'700' }}>
                      {checkedCount}/{totalItems}
                    </span>
                  </div>
                  <div style={{ background:T.bd, borderRadius:'999px',
                    height:'4px', overflow:'hidden' }}>
                    <div style={{ height:'100%', borderRadius:'999px',
                      background: allChecked
                        ? T.gn
                        : T.or,
                      width:`${(checkedCount / totalItems) * 100}%`,
                      transition:'width 0.4s, background 0.3s' }}/>
                  </div>
                </div>
              </div>

              <div style={{ padding:'8px' }}>
                {CHECKLIST_ITEMS.map((item, i) => {
                  const checked  = checklist[item.id]
                  const isLast   = i === CHECKLIST_ITEMS.length - 1
                  const disabled = item.id === 'comparatives' && first_year

                  // Phase R3: auto-flag tax_computation based on DT traffic light
                  const dtWarning = item.id === 'tax_computation'
                    && deferredTax
                    && deferredTax.traffic_light === 'red'
                    && !checked

                  return (
                    <div
                      key={item.id}
                      onClick={() => !disabled && toggleCheck(item.id)}
                      style={{ display:'flex', gap:'12px', alignItems:'flex-start',
                        padding:'12px', borderRadius:'8px',
                        cursor: disabled ? 'default' : 'pointer',
                        marginBottom: isLast ? '0' : '2px',
                        background: checked ? alpha(T.gn, 0.06)
                          : dtWarning ? T.rdd
                          : 'transparent',
                        border:`1px solid ${checked ? alpha(T.gn, 0.2)
                          : dtWarning ? alpha(T.rd, 0.25)
                          : 'transparent'}`,
                        transition:'all 0.15s',
                        opacity: disabled ? 0.5 : 1 }}
                      onMouseEnter={e => {
                        if (!checked && !disabled)
                          e.currentTarget.style.background = T.s2
                      }}
                      onMouseLeave={e => {
                        if (!checked)
                          e.currentTarget.style.background = dtWarning ? T.rdd : 'transparent'
                      }}>

                      <div style={{ width:'20px', height:'20px', borderRadius:'6px',
                        flexShrink:0, marginTop:'1px',
                        border:`1.5px solid ${checked ? T.gn : dtWarning ? T.rd : T.bd}`,
                        background: checked ? T.gn : 'transparent',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        transition:'all 0.2s' }}>
                        {checked && (
                          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                            <path d="M1 4L4 7.5L10 1"
                              style={{ stroke: T.onpri }} strokeWidth="1.8"
                              strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>

                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'13px', fontWeight:'600',
                          color: checked ? T.gn : dtWarning ? T.rd : T.tx,
                          marginBottom:'2px', lineHeight:'1.4',
                          transition:'color 0.2s' }}>
                          {item.label}
                          {disabled && (
                            <span style={{ fontSize:'10px', color:T.mt,
                              fontWeight:'400', marginLeft:'6px' }}>
                              (first year — N/A)
                            </span>
                          )}
                          {dtWarning && (
                            <span style={{ fontSize:'10px', color:T.rd,
                              fontWeight:'400', marginLeft:'6px' }}>
                              — DT variance flagged
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:'10px', color:T.mt, lineHeight:'1.5' }}>
                          {item.sub}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              <div style={{ padding:'16px 20px', borderTop:`1px solid ${T.bd}` }}>
                {allChecked && unmappedAccounts.length === 0 && unconfirmedLowConfidenceAccounts.length === 0 ? (
                  <button onClick={handleProceed}
                    style={{ width:'100%', padding:'12px', borderRadius:'8px',
                      border:'none',
                      background:T.prg,
                      color:T.onpri, fontFamily:"'Outfit',sans-serif",
                      fontSize:'14px', fontWeight:'800', cursor:'pointer',
                      boxShadow:`0 4px 20px ${alpha(T.pri, 0.35)}` }}>
                    Generate PDF →
                  </button>
                ) : (
                  <div style={{ textAlign:'center', fontSize:'13px', color:T.mt,
                    padding:'4px 0', lineHeight:'1.6' }}>
                    {!allChecked && `${totalItems - checkedCount} disclosure item${totalItems - checkedCount !== 1 ? 's' : ''} remaining. `}
                    {unmappedAccounts.length > 0 && `${unmappedAccounts.length} account(s) unmapped. `}
                    {unconfirmedLowConfidenceAccounts.length > 0 && `${unconfirmedLowConfidenceAccounts.length} low-confidence mapping(s) need confirmation.`}
                  </div>
                )}
                {checklistSaveError && (
                  <div style={{ marginTop:'10px', fontSize:'12px', color:T.rd,
                    textAlign:'center', lineHeight:'1.5' }}>
                    {checklistSaveError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
